import * as React from 'react';
import { Checkbox, Grid, createStyles } from '@mantine/core';
import { NumberFormatValues } from 'react-number-format';

// Components
import { Select } from './Select';
import { NumericFormatInput } from './NumericFormatInput';
import { YesNoRadio } from './YesNoRadio';

// Styles
import { getCommonFieldStyles } from '../helpers/fieldStyles';

// Hooks
import { useFieldTranslation } from '../hooks/useFieldTranslation';
import { useEnumDataSet } from '../hooks/useEnumDataSet';

// Types
import { GenericFieldProps } from '../types';

const useStyles = createStyles((theme) => {
    const commonFieldStyles = getCommonFieldStyles(theme);

    return {
        root: commonFieldStyles.root,
        input: commonFieldStyles.input,
        readonlyInput: commonFieldStyles.readonlyInput,
        required: commonFieldStyles.required,
        hiddenDisplay: commonFieldStyles.hiddenDisplay,
        label: commonFieldStyles.label,
        error: commonFieldStyles.error,
        description: commonFieldStyles.description,
        icon: commonFieldStyles.icon,
        body: {
            ...commonFieldStyles.wrapper,
            paddingTop: '27px',
            height: '2.2rem',
            lineHeight: '2.2rem',
            alignItems: 'center',
            border: '0.0625rem solid transparent',
            boxSizing: 'content-box'
        },
        readonlyBody: commonFieldStyles.readonlyWrapper
    };
});

export interface CustomPercentageOwnershipInputProps extends GenericFieldProps {
    key?: string;
    readOnly?: boolean;
    selectProps: GenericFieldProps;
    value?: string;
    onChange: (val: number | undefined) => void;
    alwaysTrue?: boolean;
    required?: boolean;
    error?: string;
}

const getSelectValue = (val?: string | { label: string; value: string }) => {
    if (!val) return;
    return typeof val === 'string' ? val : val.value;
};

export const PercentageOwnershipInput: React.FC<CustomPercentageOwnershipInputProps> = ({
    readOnly,
    selectProps,
    value,
    alwaysTrue,
    ...props
}) => {
    const [isOwner, setIsOwner] = React.useState<boolean | undefined>(alwaysTrue ? true : undefined);
    const { getTranslatedDataSet } = useEnumDataSet();
    const selectData = React.useMemo(() => getTranslatedDataSet('YES_OR_NO_BINARY'), []);
    const [loaded, setLoaded] = React.useState(false);

    React.useEffect(() => {
        if (!alwaysTrue && value == '0') {
            setIsOwner(false);
        } else if (value && isOwner !== true) {
            setIsOwner(true);
        }
    }, []);

    React.useEffect(() => {
        if (loaded && !isOwner) {
            props.onChange(0);
        }
    }, [isOwner]);

    React.useLayoutEffect(() => {
        setLoaded(true);
    }, []);

    return (
        <Grid>
            <Grid.Col style={{ flex: '0 1 11rem' }}>
                {/* <Select
                    data={selectData}
                    value={isOwner}
                    onChange={(val: string) => setIsOwner(val)}
                    readOnly={readOnly}
                    {...selectProps}
                /> */}
                <YesNoRadio
                    value={isOwner}
                    onChange={(val: string | boolean) => setIsOwner(val as boolean)}
                    readOnly={readOnly}
                    disabled={alwaysTrue}
                    error={props?.error}
                    required={props?.required}
                    {...selectProps}
                />
            </Grid.Col>
            {isOwner ? (
                <Grid.Col md={6} lg={3}>
                    <NumericFormatInput
                        labelKey={props.labelKey}
                        onValueChange={(values: NumberFormatValues) => {
                            props.onChange?.(values.floatValue ?? 0);
                        }}
                        suffix={' %'}
                        prefix={''}
                        decimalScale={0}
                        isAllowed={(values: NumberFormatValues) => {
                            const { floatValue } = values;

                            return typeof floatValue === 'undefined' || floatValue <= 100;
                        }}
                        value={value}
                        error={props?.error}
                    />
                </Grid.Col>
            ) : null}
        </Grid>
    );
};
